/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useLocation, Link, useHistory } from 'react-router-dom';
import { Breadcrumb } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  IconName,
  IconPrefix,
  IconProp,
} from '@fortawesome/fontawesome-svg-core';

import IconButton from '~components/_common/IconButton/IconButton';
import { getParentsIds } from '~src/utils/tree';
import navigationItems, { NavItemType } from '~src/data/navigationItems';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { useProjectId } from '~src/hooks/useProjectId';
import navigationItemsSu from '~src/data/navigationItemsSu';
import { useStyles } from '~src/hooks/useStyles';
import { useTemplates } from '~src/hooks/api-hooks/useTemplates';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { useSectorTree } from '~src/hooks/api-hooks/useSectorTree';

import FolderTender from '~src/assets/nav-icons/folder-tender.svg';
import Folder from '~src/assets/nav-icons/folder.svg';
import FolderTenderHammer from '~src/assets/nav-icons/folder-tender-hammer.svg';
import TenderRegistration from '~src/assets/nav-icons/file-tender-hammer.svg';
import Tender from '~src/assets/nav-icons/module-tender-hammer-color.svg';
import ProjectSrc from '~src/assets/nav-icons/project.png';

import { useTenders } from '~src/hooks/api-hooks/useTenders';
import { useTenderFoldersFoldersId } from '~src/hooks/api-hooks/useTenderFoldersFoldersId';
import { useTenderFoldersFolders } from '~src/hooks/api-hooks/useTenderFoldersFolders';

import { styleFn } from '../style';
import { DEFULT_PROJECT_TENDER_TAB_KEY } from '~components/molecules/Tabs/TenderTabs/TenderTabs';

const DefaultBreadcrumbs = () => {
  const style = useStyles(styleFn);
  const history = useHistory();

  const { pathname } = useLocation();
  const projectId = useProjectId();
  const intl = useIntl();
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const templatesQuery = useTemplates(undefined, { enabled: false });
  const projectQuery = useProject({ enabled: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<StateBreadCrumb[]>([]);
  const pathSegments = history.location.pathname.split('/').filter(Boolean); // Split and remove empty segments
  const penultimateSegment = pathSegments[pathSegments.length - 3];
  const tenderIdMatch = window?.location?.pathname?.match(/tenders\/(\d+)/);
  const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';

  const isOnUnassgignedPage = pathSegments[2] === 'tender-folders';

  const foldersQueryUnasignedResult = useTenderFoldersFolders(
    {
      root_folder_id: penultimateSegment,
    },
    { enabled: isOnUnassgignedPage }
  );

  const foldersTenderFoldersFolders = useTenderFoldersFoldersId(
    {
      tender_id: tenderId,
      root_folder_id:
        pathSegments[5] === 'tender-folders' ? penultimateSegment : undefined,
    },
    { enabled: pathSegments[5] === 'tender-folders' }
  );

  const tendersQuery = useTenders(
    {
      before:
        window.sessionStorage.getItem('timeMachineDate') !== null
          ? Number(window.sessionStorage.getItem('timeMachineDate'))
          : undefined,
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: pathSegments[2] === 'tenders' }
  );

  const sectorTreeQuery = useSectorTree(String(projectId), {
    enabled: pathSegments[2] === 'section-modules',
  });

  // Helper to find sector and its parents in the tree
  const findSectorPath = (sectorId: string, sectorTree: any[]) => {
    for (const block of sectorTree) {
      for (const subdivision of block.subdivisions) {
        const sector = subdivision.sectors.find(
          (s: any) => String(s.sector_id) === sectorId
        );
        if (sector) {
          return {
            block,
            subdivision,
            sector,
          };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    // split url paths
    const beforePaths = pathname.split('/').filter(Boolean);
    const paths = beforePaths
      .map((path, index) => {
        if (path === 'tender-folders' && index > 0) {
          return ['tenders', path];
        }
        return path;
      })
      .flat();

    // SPECIAL case for public share page, display a disabled "Shared with me" item
    if (paths[0] === 'share') {
      const sharedNavItem = navigationItems.find(
        (navItem) => navItem.key === 'shared-with-me'
      ) as typeof navigationItems[0];

      return setBreadcrumbs([
        {
          name: intl.formatMessage({ id: sharedNavItem.nameTerm }),
          path: '',
          navItem: sharedNavItem,
          disabled: true,
        },
      ]);
    }

    // /projects/:projectId, should have Home item
    if (paths.length < 3) {
      setBreadcrumbs([
        {
          name: projectQuery.data?.name || intl.formatMessage({ id: 'home' }),
          path: '/',
          navItem: homeNavItem,
        },
      ]);
      return;
    }

    // e.g. /projects/:projectId/archive

    if (paths[2]) {
      const crumbs: StateBreadCrumb[] = [];

      // SPECIAL CASE: Handle modules-sectors breadcrumbs FIRST (before navItem lookup)
      if (paths[2] === 'section-modules' && paths.length >= 6) {
        // URL: /projects/{projectId}/section-modules/sector/{sectorId}/registrations

        // 1. Add Dashboard breadcrumb (clickable, links to project home)
        crumbs.push({
          name: projectQuery.data?.name || intl.formatMessage({ id: 'home' }),
          path: `/projects/${projectId}`,
          navItem: homeNavItem,
          clickable: true,
        });

        // 2. Add "Modules-Sectors" breadcrumb with custom icon
        crumbs.push({
          name: intl.formatMessage({ id: 'moduls-sectors' }),
          path: `/projects/${projectId}/section-modules`,
          navItem: {
            icon: ['fad', 'map'] as [IconPrefix, IconName],
            color: 'blue',
          },
          clickable: false,
        });

        // 3. Build the full hierarchy breadcrumb path
        const sectorIdentifier = paths[4]; // "null" or numeric ID

        if (sectorIdentifier === 'null') {
          // Outside sectors - simple case
          crumbs.push({
            name: intl.formatMessage({ id: 'outside-sectors' }),
            path: `/projects/${projectId}/section-modules/sector/${sectorIdentifier}/registrations`,
            navItem: {
              icon: ['fad', 'map-location'] as [IconPrefix, IconName],
              color: 'blue',
            },
            clickable: false,
          });
        } else if (sectorTreeQuery.data) {
          // Regular sector - find in tree and build full path
          const sectorPath = findSectorPath(
            sectorIdentifier,
            sectorTreeQuery.data
          );

          if (sectorPath) {
            const { block, subdivision, sector } = sectorPath;

            // Add Block (division) breadcrumb
            crumbs.push({
              name: block.block_name,
              path: `/projects/${projectId}/section-modules`, // Not navigable, but needs path
              navItem: {
                icon: ['fad', 'layer-group'] as [IconPrefix, IconName],
                color: 'blue',
              },
              clickable: false,
            });

            // Add Subdivision breadcrumb
            crumbs.push({
              name: subdivision.subdivision_name,
              path: `/projects/${projectId}/section-modules`, // Not navigable, but needs path
              navItem: {
                icon: ['fad', 'layer-group'] as [IconPrefix, IconName], // Use layer-group or custom icon
                color: 'blue',
              },
              clickable: false,
            });

            // Add Sector breadcrumb (final/current)
            crumbs.push({
              name: sector.sector_name,
              path: `/projects/${projectId}/section-modules/sector/${sectorIdentifier}/registrations`,
              navItem: {
                icon: ['fad', 'map-location-dot'] as [IconPrefix, IconName],
                color: 'blue',
              },
              clickable: false,
            });
          } else {
            // Fallback if sector not found in tree
            crumbs.push({
              name: `Sector ${sectorIdentifier}`,
              path: `/projects/${projectId}/section-modules/sector/${sectorIdentifier}/registrations`,
              navItem: {
                icon: ['fad', 'map-location-dot'] as [IconPrefix, IconName],
                color: 'blue',
              },
              clickable: false,
            });
          }
        } else {
          // Loading or no data - show ID as fallback
          crumbs.push({
            name: `Sector ${sectorIdentifier}`,
            path: `/projects/${projectId}/section-modules/sector/${sectorIdentifier}/registrations`,
            navItem: {
              icon: ['fad', 'map-location-dot'] as [IconPrefix, IconName],
              color: 'blue',
            },
            clickable: false,
          });
        }

        // Set breadcrumbs and return early to skip general navigation logic
        setBreadcrumbs(crumbs);
        sessionStorage.setItem('breadcrumbPath', JSON.stringify(crumbs));
        setTimeout(() => {
          containerRef.current?.scroll(containerRef.current.scrollWidth, 0);
        }, 300);
        return;
      }

      // get root navItem e.g. archive, recent etc.
      const navItem =
        window.sessionStorage.getItem('appState') === 'superUser'
          ? navigationItemsSu.find(
              (v) =>
                v.key === paths[2] || v.path === '/' + paths[2] + '/' + paths[3]
            )
          : navigationItems.find(
              (v) =>
                v.key === paths[2] || v.path === '/' + paths[2] + '/' + paths[3]
            );

      if (!navItem) return;
      const rootPath =
        paths[0] === 'projects' && paths[2] === 'archive'
          ? `/projects/${projectId}/archive`
          : navItem.path;

      crumbs.push({
        name: intl.formatMessage({ id: navItem.nameTerm }),
        path: rootPath,
        navItem: {
          ...navItem,
          icon:
            Array.isArray(navItem?.icon) && navItem.icon.length === 2
              ? [navItem.icon[0] as IconPrefix, navItem.icon[1] as IconName]
              : undefined,
        },
      });

      if (paths[3] && paths[2] === 'templates' && templatesQuery.data) {
        const template = templatesQuery.data?.find(
          (v) => String(v.id) === paths[3]
        );
        if (template) {
          crumbs.push({
            name: template.name || 'Unknown',
            path: `/templates/${template.id}`,
          });
        }
      }

      // paths[3] is folderId
      if (paths[3] && paths[2] === 'archive' && foldersQuery.data) {
        const parentIds = getParentsIds(foldersQuery.data, paths[3]);

        // create breadcrumbs for parents
        const parentCrumbs = parentIds
          .map((id) => {
            const dbFolder = foldersQuery.data?.find(
              (v) => String(v.id) === id
            );
            if (!dbFolder) return undefined;

            return {
              name: dbFolder.name || 'Unknown',
              path: `/projects/${projectId}/archive/${dbFolder.id}`,
            };
          })
          .filter(Boolean) as StateBreadCrumb[];

        crumbs.push(...parentCrumbs);

        // last, add current folder
        const folder = foldersQuery.data?.find(
          (v) => String(v.id) === paths[3]
        );
        if (folder)
          crumbs.push({
            iconKey: folder?.option?.is_tender_root_folder
              ? 'FolderTenderHammer'
              : folder?.option?.is_tender_folder
              ? 'FolderTender'
              : 'Folder',

            name: folder.name || 'Unknown',
            path: `/projects/${projectId}/archive/${folder.id}`,
          });
      }

      if (
        paths[3] &&
        paths[3] === 'tender-folders' &&
        foldersQueryUnasignedResult.data
      ) {
        const parentIds = getParentsIds(
          foldersQueryUnasignedResult.data || [],
          paths[paths.length - 1]
        );

        // create breadcrumbs for parents
        const parentCrumbs = parentIds
          .map((id) => {
            const dbFolder = foldersQueryUnasignedResult.data?.find(
              (v) => String(v.id) === id
            );
            if (!dbFolder) return undefined;

            return {
              name: dbFolder.name || 'Unknown',
              path: `/projects/${projectId}/tender-folders/${
                dbFolder.parent_id || dbFolder.id
              }/folders/${dbFolder.id}`,
            };
          })
          .filter(Boolean) as StateBreadCrumb[];

        crumbs.push(...parentCrumbs);

        // last, add current folder
        const folder = foldersQueryUnasignedResult?.data?.find(
          (v) => String(v.id) === paths[paths.length - 1]
        );

        if (folder) {
          //crumbs.splice(1, 1);
          crumbs.push({
            name: folder.name || 'Unknown',
            path: `/projects/${projectId}/tender-folders/${
              folder.parent_id || folder.id
            }/folders/${folder.id}`,
          });
        }
      }
      if (
        paths.length > 4 &&
        paths[3] &&
        paths[2] === 'tenders' &&
        paths[3] !== 'tender-folders' &&
        tendersQuery.data
      ) {
        let foundFolder;
        const currentId =
          paths.length === 5 ? paths[3] : paths[paths.length - 1];
        let parentIds: string[] = [];

        if (
          paths.length >
          5 /*  &&
          paths[paths.length - 1] === paths[paths.length - 3] */
        ) {
          parentIds = [paths[3]];

          for (const tender of tendersQuery.data) {
            foundFolder = tender.tender_root_folders.find(
              (folder) =>
                folder.folder_id === parseInt(paths[paths.length - 1], 10)
            );

            if (foundFolder) {
              break;
            }
          }
        }

        if (
          paths.length > 5 &&
          paths[paths.length - 1] !== paths[paths.length - 3]
        ) {
          parentIds = [
            ...parentIds,
            ...getParentsIds(
              foldersTenderFoldersFolders.data || [],
              paths[paths.length - 1]
            ),
          ];
        }
        // create breadcrumbs for parents
        const parentCrumbs = parentIds
          .map((id) => {
            const dbFolder = tendersQuery.data?.find(
              (v) => String(v.id) === id
            );
            let dbFoldersTenderFoldersFolders;
            if (!dbFolder) {
              dbFoldersTenderFoldersFolders = foldersTenderFoldersFolders.data?.find(
                (v) => String(v.id) === id
              );
              if (!dbFoldersTenderFoldersFolders) return undefined;
              return {
                iconKey: 'FolderTender',
                name: dbFoldersTenderFoldersFolders.name || 'Unknown',
                path: `/projects/${projectId}/tenders/${parentIds[0]}/documents/tender-folders/${paths[7]}/folders/${dbFoldersTenderFoldersFolders.id}`,
              };
            }
            if (!dbFolder) return undefined;

            return {
              iconKey: 'TenderRegistration',
              name: dbFolder?.reference
                ? dbFolder.reference + ' - ' + (dbFolder?.title || 'Unknown')
                : dbFolder?.title || 'Unknown',
              path: `/projects/${projectId}/tenders/${dbFolder.id}/${DEFULT_PROJECT_TENDER_TAB_KEY}`,
            };
          })
          .filter(Boolean) as StateBreadCrumb[];

        crumbs.push(...parentCrumbs);

        // last, add current folder
        const folder = tendersQuery?.data?.find(
          (v) => String(v.id) === currentId
        );

        if (folder) {
          if (paths.length === 5) {
            crumbs.push({
              iconKey: 'TenderRegistration',
              name: folder?.reference
                ? folder.reference + ' - ' + (folder.title || 'Unknown')
                : folder.title || 'Unknown',
              path: `/projects/${projectId}/tenders/${folder.id}/${DEFULT_PROJECT_TENDER_TAB_KEY}`,
            });
          }
        } else {
          if (foundFolder) {
            crumbs.push({
              iconKey: 'FolderTenderHammer',

              name: foundFolder.alias_title || 'Unknown',
              path: `/projects/${projectId}/tenders/${parentIds[0]}/documents/tender-folders/${foundFolder.folder_id}/folders/${foundFolder.folder_id}`,
            });
          } else {
            const folderTenderFoldersFolders = foldersTenderFoldersFolders?.data?.find(
              (v) => String(v.id) === currentId
            );
            crumbs.push({
              iconKey: 'FolderTender',
              name: folderTenderFoldersFolders?.name || 'Unknown',
              path: `/projects/${projectId}/tenders/${
                parentIds[0]
              }/documents/tender-folders/${
                paths[paths.length - 3]
              }/folders/${currentId}`,
            });
          }
        }
      }

      setBreadcrumbs(crumbs);

      sessionStorage.setItem('breadcrumbPath', JSON.stringify(crumbs));
      setTimeout(() => {
        containerRef.current?.scroll(containerRef.current.scrollWidth, 0);
      }, 300);
    }
  }, [
    pathname,
    foldersQuery.data,
    intl,
    projectQuery.data,
    foldersQueryUnasignedResult.data,
    tendersQuery.data,
    foldersTenderFoldersFolders.data,
    sectorTreeQuery.data,
    projectId,
  ]);

  return (
    <div css={style.component}>
      <div ref={containerRef} css={style.container}>
        {!!breadcrumbs[0]?.navItem?.icon &&
        !breadcrumbs[0]?.navItem.customIcon ? (
          <IconButton
            icon={breadcrumbs[0].navItem.icon as IconProp}
            color={breadcrumbs[0].navItem.color}
            customStyle={style.icons}
            backgroundOff
          />
        ) : (
          <NavIcon
            customStyle={{
              height: '23px',
              marginTop: '2px',
              marginRight: '3px',
              width: breadcrumbs[0]?.path === '/tenders' ? '38px' : 'auto',
            }}
            icon={breadcrumbs[0]?.navItem?.customIcon || ''}
          />
        )}
        {breadcrumbs?.length === 0 && (
          <NavIcon
            customStyle={{
              height: '23px',
              marginTop: '2px',
              marginRight: '3px',
              //width: breadcrumbs[0]?.path === '/tenders' ? '38px' : 'auto',
            }}
            icon={ProjectSrc}
          />
        )}
        <Breadcrumb css={{ whiteSpace: 'nowrap' }} separator=">">
          {breadcrumbs?.length === 0 && (
            <Breadcrumb.Item css={{ display: 'inline-block' }} key={`project`}>
              <Link to={`/projects/${projectId}`}>
                {projectQuery.data?.name}
              </Link>
            </Breadcrumb.Item>
          )}
          {breadcrumbs.map((item) => {
            const icons = {
              FolderTender,
              Tender,
              FolderTenderHammer,
              TenderRegistration,
              Folder,
            };
            return !item.disabled ? (
              <Breadcrumb.Item
                css={{ display: 'inline-block' }}
                key={`${item.path}-${item.name}`}>
                {item === breadcrumbs[breadcrumbs.length - 1] &&
                  item !== breadcrumbs[0] &&
                  (item.navItem?.icon ? (
                    <IconButton
                      icon={item.navItem.icon as IconProp}
                      color={item.navItem.color}
                      customStyle={{
                        height: '17px',
                        marginTop: '2px',
                        width: '30px',
                      }}
                      backgroundOff
                    />
                  ) : (
                    <NavIcon
                      customStyle={{
                        height: ' 23px',
                        marginTop: '2px',
                        width: '38px',
                      }}
                      icon={
                        icons[
                          breadcrumbs[breadcrumbs.length - 1]?.iconKey ||
                            'FolderTender'
                        ]
                      }
                    />
                  ))}
                {item.clickable !== false ? (
                  <Link to={item.path}>{item.name}</Link>
                ) : (
                  <span>{item.name}</span>
                )}
              </Breadcrumb.Item>
            ) : (
              <Breadcrumb.Item key={`${item.path}-${item.name}`}>
                {item.name}
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
        <div css={{ minWidth: 10 }}></div>
      </div>
    </div>
    /*  </div> */
  );
};

export default DefaultBreadcrumbs;
interface StateBreadCrumb {
  name?: string;
  path: string;
  /** for first level items e.g. archive, recent */
  navItem?: Partial<NavItemType>;
  disabled?: boolean;
  iconKey?: string;
  clickable?: boolean;
}

const homeNavItem = {
  icon: ['fad', 'house'] as [IconPrefix, IconName],
  color: 'darkGray',
};
